'use client';
import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { useSession } from 'next-auth/react';

export const LANGUAGES = ['en', 'tr', 'fr', 'de', 'es', 'it', 'zh', 'ja', 'ko', 'hi', 'ru', 'ar', 'pt'] as const;
export type Language = (typeof LANGUAGES)[number];

export const NOTE_NAMING_STYLES = ['abc', 'dorm'] as const;
export type NoteNamingStyle = (typeof NOTE_NAMING_STYLES)[number];

export interface Preferences {
  language: Language;
  noteNaming: NoteNamingStyle;
  bpm: number;
  instrument: string;
  selectedMxlUrl: string;
}

const DEFAULTS: Preferences = {
  language: 'en',
  noteNaming: 'abc',
  bpm: 90,
  instrument: '',
  selectedMxlUrl: '',
};

const STORAGE_KEY = 'muzik-app:preferences';

interface Ctx {
  prefs: Preferences;
  setPrefs: (patch: Partial<Preferences>) => void;
}

const PreferencesContext = createContext<Ctx | null>(null);

function readLocalStorage(): Preferences {
  if (typeof window === 'undefined') return DEFAULTS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...DEFAULTS, ...(JSON.parse(raw) as Partial<Preferences>) };
  } catch {}
  return DEFAULTS;
}

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const { status } = useSession();
  // Always start with DEFAULTS on first render (server + client hydration both see the
  // same value). After mount, overwrite with whatever is stored in localStorage so the
  // real user preferences take effect without causing a hydration mismatch.
  const [prefs, setState] = useState<Preferences>(DEFAULTS);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setState(readLocalStorage());
  }, []);

  useEffect(() => {
    if (status !== 'authenticated') return;
    fetch('/api/user/preferences')
      .then(r => (r.ok ? r.json() : null))
      .then((data: Partial<Preferences> | null) => {
        if (data && typeof data === 'object' && Object.keys(data).length > 0) {
          setState(prev => ({ ...prev, ...data }));
        }
      })
      .catch(() => {});
  }, [status]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch {}
  }, [prefs]);

  const setPrefs = (patch: Partial<Preferences>) => {
    setState(prev => ({ ...prev, ...patch }));
    if (status === 'authenticated') {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        fetch('/api/user/preferences', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(patch),
        }).catch(() => {});
      }, 300);
    }
  };

  return (
    <PreferencesContext.Provider value={{ prefs, setPrefs }}>
      {children}
    </PreferencesContext.Provider>
  );
}

export function usePreferences() {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error('usePreferences must be used inside PreferencesProvider');
  return ctx;
}
